import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import {
  excludeArchivedTracePreviews,
  isArchivedTracePreview,
} from '@terra/ui-design-system';
import {
  countSelectedExecutions,
  isAbortableExecution,
  isRerunableExecution,
  isResumableExecution,
  selectMutableExecutions,
} from './executions-list.model';
import { createTabScopedStore } from '../../../store/tab/createTabScopedStore';

type TraceSelectionStoreState = {
  selectedTraces: TracePreviewDto[];
  isAllFilteredSelected: boolean;
  excludedThreadIds: string[];
};

const traceSelectionDefaults: TraceSelectionStoreState = {
  selectedTraces: [],
  isAllFilteredSelected: false,
  excludedThreadIds: [],
};

const useTraceTabScopedSelectionStoreBase =
  createTabScopedStore<TraceSelectionStoreState>(traceSelectionDefaults);

export function useExecutionsSelection({
  resetKey,
  selectableTotalCount,
  fetchAllTraces,
}: {
  resetKey: string;
  selectableTotalCount: number;
  fetchAllTraces: () => Promise<TracePreviewDto[]>;
}) {
  const { filters, update } = useTraceTabScopedSelectionStoreBase();
  const { isAllFilteredSelected } = filters;
  const previousResetKeyRef = useRef(resetKey);
  const selectionStateRef = useRef({
    selectedTraces: filters.selectedTraces,
    excludedThreadIds: filters.excludedThreadIds,
    isAllFilteredSelected,
  });

  selectionStateRef.current = {
    selectedTraces: filters.selectedTraces,
    excludedThreadIds: filters.excludedThreadIds,
    isAllFilteredSelected,
  };
  const selectedTraces = useMemo(
    () =>
      new Map(
        filters.selectedTraces.map((trace) => [trace.threadId, trace] as const),
      ),
    [filters.selectedTraces],
  );
  const excludedThreadIds = useMemo(
    () => new Set(filters.excludedThreadIds),
    [filters.excludedThreadIds],
  );

  useEffect(() => {
    if (previousResetKeyRef.current === resetKey) {
      return;
    }

    previousResetKeyRef.current = resetKey;
    update(traceSelectionDefaults);
  }, [resetKey, update]);

  const selectedCount = countSelectedExecutions({
    isAllFilteredSelected,
    selectableTotalCount,
    excludedCount: excludedThreadIds.size,
    selectedCount: selectedTraces.size,
  });
  const areAllSelected =
    isAllFilteredSelected &&
    excludedThreadIds.size === 0 &&
    selectableTotalCount > 0;

  const eligibleCounts = useMemo(() => {
    if (isAllFilteredSelected) {
      return { resumable: null, rerunable: null, abortable: null };
    }

    const traces = Array.from(selectedTraces.values());

    return {
      resumable: traces.filter(isResumableExecution).length,
      rerunable: traces.filter(isRerunableExecution).length,
      abortable: traces.filter(isAbortableExecution).length,
    };
  }, [isAllFilteredSelected, selectedTraces]);

  const isTraceSelected = useCallback(
    (trace: TracePreviewDto) =>
      !isArchivedTracePreview(trace) &&
      (isAllFilteredSelected
        ? !excludedThreadIds.has(trace.threadId)
        : selectedTraces.has(trace.threadId)),
    [excludedThreadIds, isAllFilteredSelected, selectedTraces],
  );

  const toggleTrace = useCallback(
    ({ trace, selected }: { trace: TracePreviewDto; selected: boolean }) => {
      if (isArchivedTracePreview(trace)) return;

      const current = selectionStateRef.current;

      if (current.isAllFilteredSelected) {
        const excludedThreadIds = selected
          ? current.excludedThreadIds.filter((id) => id !== trace.threadId)
          : current.excludedThreadIds.includes(trace.threadId)
            ? current.excludedThreadIds
            : [...current.excludedThreadIds, trace.threadId];

        selectionStateRef.current = { ...current, excludedThreadIds };
        update({ excludedThreadIds });

        return;
      }

      const selectedTraces = excludeArchivedTracePreviews(
        selected
          ? current.selectedTraces.some(
              (selectedTrace) => selectedTrace.threadId === trace.threadId,
            )
            ? current.selectedTraces
            : [...current.selectedTraces, trace]
          : current.selectedTraces.filter(
              (selectedTrace) => selectedTrace.threadId !== trace.threadId,
            ),
      );

      selectionStateRef.current = { ...current, selectedTraces };
      update({ selectedTraces });
    },
    [update],
  );

  const handleDeselectAll = useCallback(() => {
    selectionStateRef.current = {
      selectedTraces: [],
      excludedThreadIds: [],
      isAllFilteredSelected: false,
    };
    update(traceSelectionDefaults);
  }, [update]);

  const selectAll = useCallback(
    (selected: boolean) => {
      if (!selected) {
        handleDeselectAll();

        return;
      }

      selectionStateRef.current = {
        selectedTraces: [],
        excludedThreadIds: [],
        isAllFilteredSelected: true,
      };
      update({
        isAllFilteredSelected: true,
        selectedTraces: [],
        excludedThreadIds: [],
      });
    },
    [handleDeselectAll, update],
  );

  const getSelectedTraces = useCallback(async (): Promise<
    TracePreviewDto[]
  > => {
    if (selectedCount === 0) {
      return [];
    }

    if (!isAllFilteredSelected) {
      return excludeArchivedTracePreviews(Array.from(selectedTraces.values()));
    }

    return selectMutableExecutions({
      traces: await fetchAllTraces(),
      excludedThreadIds,
      selectedThreadIds: new Set(),
      isAllFilteredSelected,
    });
  }, [
    excludedThreadIds,
    fetchAllTraces,
    isAllFilteredSelected,
    selectedCount,
    selectedTraces,
  ]);

  return {
    selectedCount,
    areAllSelected,
    isAllFilteredSelected,
    resumableCount: eligibleCounts.resumable,
    rerunableCount: eligibleCounts.rerunable,
    abortableCount: eligibleCounts.abortable,
    isTraceSelected,
    toggleTrace,
    selectAll,
    getSelectedTraces,
    handleDeselectAll,
  };
}

export type ExecutionsSelection = ReturnType<typeof useExecutionsSelection>;
